import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'https://heysalad-admin.heysalad-o.workers.dev'

export default function Approve() {
  const { token } = useParams<{ token: string }>()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const [companyName, setCompanyName] = useState('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('Invalid approval link')
      return
    }

    const approveSignup = async () => {
      try {
        const response = await fetch(`${API_URL}/api/signup/approve/${token}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })

        const data = await response.json()

        if (data.success) {
          setStatus('success')
          setCompanyName(data.data?.company_name || 'Your company')
          setMessage(data.data?.message || 'Company approved successfully!')
        } else {
          setStatus('error')
          setMessage(data.error || 'Failed to approve signup')
        }
      } catch (error) {
        setStatus('error')
        setMessage('Network error. Please try again.')
      }
    }

    approveSignup()
  }, [token])

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <img 
          src="https://pay.heysalad.cash/HeySalad%20Logo%20Black.png" 
          alt="HeySalad" 
          className="h-12 mx-auto mb-6"
        />

        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-red-500 mx-auto mb-4 animate-spin" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Processing approval...
            </h1>
            <p className="text-gray-600">Please wait while we set up the company.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {companyName} is now on HeySalad! 🎉
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <p className="text-sm text-gray-500">
              The person who requested this signup will receive an email with next steps.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Approval Failed
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact{' '}
              <a href="mailto:support@heysalad.io" className="text-red-500 hover:underline">
                support@heysalad.io
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
